<template>
  <div role="form">
    <div :class="getClass()">
      <div>
        <input :id="pondId"
               type="file"
               ref="pond"
               multiple
        />
        <p v-show="error" class="text-danger">
          {{ errorMessage }}
        </p>
      </div>
    </div>
  </div>
</template>
<script>
// Import Vue FilePond
import * as FilePond from 'filepond';

// Import plugins
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
import FilePondPluginImageEditor from '@pqina/filepond-plugin-image-editor';
import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size/dist/filepond-plugin-file-validate-size.js';
import FilePondPluginFilePoster from 'filepond-plugin-file-poster/dist/filepond-plugin-file-poster';

// Import Pintura
import {
  // editor
  createDefaultImageReader,
  createDefaultImageWriter,
  locale_en_gb,

  // plugins
  setPlugins,
  plugin_crop,
  plugin_crop_locale_en_gb,

  // filepond
  openEditor,
  processImage,
  createDefaultImageOrienter,
} from '@pqina/pintura';

import RSVP from 'rsvp';
import { isHeicFile, convertHeicToJpeg } from '../utilities/heic';

// here we set the available plugins
setPlugins(plugin_crop);

// Create FilePond component
FilePond.registerPlugin(
  FilePondPluginFileValidateType,
  FilePondPluginImageEditor,
  FilePondPluginFileValidateSize,
  FilePondPluginFilePoster,
);

// This is intended to be a general purpose pintura uploader as opposed to the profile photo which has specific structure.
// eventually this should get threaded into that as well
export default {
  name: 'pintura-photo-uploader',
  props: {
    endpoint: {
      type: String,
      required: true,
    },
    accessKey: {
      type: String,
      required: true,
    },
    cloudfrontBaseUrl: {
      type: String,
      required: true,
    },
    existingAttachments: {
      type: Array,
      default() {
        return [];
      },
    },
    prefix: {
      type: String,
      required: true,
    },
    imageCropAspectRatio: {
      type: String,
      default: undefined,
    },
    maxFiles: {
      type: Number,
      default: 1,
    },
    attachableId: {
      type: Number,
      required: false,
    },
    attachableType: {
      type: String,
      required: false,
    },
    pondId: {
      type: String,
      default: 'pintura-uploader-1',
    },
    acceptedFileTypes: {
      type: String,
      default: 'image/*, application/pdf',
    },
    // Set false to upload the original bytes untouched (e.g. print-ready logo
    // art) instead of re-encoding through the Pintura editor
    editImages: {
      type: Boolean,
      default: true,
    },
    maxFileSize: {
      type: String,
      default: '15MB',
    },
    labelIdle: {
      type: String,
      default: 'Tap to upload...',
    },
  },
  components: {
    FilePond,
    RSVP,
  },
  data() {
    const vm = this;

    const getKey = function (file) {
      const filename = file.name;
      vm.mFileName = filename;

      return new RSVP.Promise((resolve, reject) => {
        const params = {
          attachment: {
            name: filename,
            prefix: vm.prefix,
            attachable_id: vm.attachableId,
            attachable_type: vm.attachableType,
          },
        };
        const url = vm.$apiService.attachmentsUrl();
        axios.post(url, params)
          .then((response) => {
            const photo = {
              _destroy: false,
              amzDate: moment(),
              amzSignature: '',
              amzPolicy: '',
              id: response.data.id,
              file_name: response.data.name,
              s3_file_name: response.data.key,
              status: 'upload_pending',
            };
            vm.photos.push(photo);
            // Announce the pending photo right away — hosts gate their save
            // on in-flight uploads, which only works if they hear about the
            // upload before it finishes
            vm.emitChanges();
            resolve(photo);
          })
          .catch((error) => {
            reject(error);
          });
      });
    };

    return {
      server: {
        load: (source, load, error, progress, abort, headers) => {
          const myRequest = new Request(source);
          fetch(myRequest).then((response) => {
            response.blob().then((myBlob) => {
              load(myBlob);
            });
          });
        },
        revert: (uniqueFileId, load, error) => {
          // TODO eventually delete from S3 that key so we don't store draft files.
          // use lodash findIndex to find the index of the photo based on the s3_file_name and then splice it out
          const index = vm.photos.findIndex((photo) => photo.s3_file_name === uniqueFileId);
          if (index !== -1) {
            vm.photos.splice(index, 1);
          }
          vm.emitChanges();
          // Should call the load method when done, no parameters required
          load();
        },
        remove: (source, load, error) => {
          const index = vm.photos.findIndex((photo) => source.includes(photo.s3_file_name));
          if (index !== -1) {
            // todo is this reactive?
            vm.photos[index]._destroy = true;
          }
          vm.emitChanges();
          // Should call the load method when done, no parameters required
          load();
        },
        process: (fieldName, file, metadata, load, error, progress, abort) => {
          getKey(file)
            .then((photo) => {
              const sixDays = photo.amzDate.utc()
                .add(6, 'days')
                .format('YYYY-MM-DDTHH:mm:ss.SSS');
              const policy_params = {
                expiration: `${sixDays}Z`,
                conditions: [
                  { acl: 'private' },
                  { bucket: vm.bucket },
                  ['starts-with', '$key', `attachments/${vm.prefix}`],
                  ['starts-with', '$Content-Type', ''],
                  /* [ "content-length-range", 0, 15728640 ], // allow up to 15 mb in size */
                  { 'x-amz-credential': vm.amzCredential(photo.amzDate) },
                  { 'x-amz-algorithm': 'AWS4-HMAC-SHA256' },
                  { 'x-amz-date': vm.amzDateAsLongISO(photo.amzDate) },
                ],
              };
              axios.post(vm.$apiService.s3SignatureUrl(), policy_params)
                .then((response) => {
                  // append this to the form with reactivity like so..
                  photo.amzPolicy = response.data.policy;
                  photo.amzSignature = response.data.signature;

                  const formData = new FormData();
                  formData.append('key', photo.s3_file_name);
                  formData.append('acl', 'private');
                  formData.append('Content-Type', file.type || 'application/octet-stream');
                  formData.append('X-Amz-Credential', vm.amzCredential(photo.amzDate));
                  formData.append('X-Amz-Algorithm', 'AWS4-HMAC-SHA256');
                  formData.append('X-Amz-Date', vm.amzDateAsLongISO(photo.amzDate));
                  formData.append('Policy', photo.amzPolicy);
                  formData.append('X-Amz-Signature', photo.amzSignature);
                  formData.append('file', file, file.name);

                  const request = new XMLHttpRequest();
                  request.open('POST', `${vm.endpoint}/`);

                  // Should call the progress method to update the progress to 100% before calling load
                  // Setting computable to false switches the loading indicator to infinite mode
                  request.upload.onprogress = (e) => {
                    progress(e.lengthComputable, e.loaded, e.total);
                  };

                  // Should call the load method when done and pass the returned server file id
                  // this server file id is then used later on when reverting or restoring a file
                  // so your server knows which file to return without exposing that info to the client
                  request.onload = function () {
                    if (request.status >= 200 && request.status < 300) {
                      const url = vm.$apiService.s3ConfirmUrl();
                      // Add a slight delay to ensure DB commit has completed
                      setTimeout(() => {
                        console.log('Confirming upload with key:', photo.s3_file_name);
                        axios.post(url, { key: photo.s3_file_name })
                          .then((response) => {
                            // the load method accepts either a string (id) or an object
                            console.log('S3 confirm success for key:', photo.s3_file_name);
                            load(photo.s3_file_name);
                            photo.status = 'uploaded';
                            vm.emitChanges();
                          })
                          .catch((errorObject) => {
                            console.error('S3 confirm failed for key:', photo.s3_file_name, errorObject);
                            error('Upload confirmation failed');
                          });
                      }, 500); // Add a half second delay
                    } else {
                      // Can call the error method if something is wrong, should exit after
                      error('oh no');
                    }
                  };

                  request.send(formData);

                  // Should expose an abort method so the request can be cancelled
                  return {
                    abort: () => {
                      // This function is entered if the user has tapped the cancel button
                      request.abort();

                      // Let FilePond know the request has been cancelled
                      abort();
                    },
                  };
                })
                .catch((errorObject) => {
                  console.log('s3 signature failed');
                  console.log(errorObject);
                  error('failed to upload');
                });
            })
            .catch((error) => {
              // handle errors on getting the key
              error('failed to start uploading');
            });
        },
      },
      // Data we manipulate for the photos on the backend
      photos: [
        /* example object
          {
            id: null,
            s3_file_name: '',
            file_name: '',
            status: '',
            // S3 specific things
            amzDate: moment(),
            amzSignature: '',
            amzPolicy: '',
            amzKey: '',
           */
      ],
      // The raw files we manipulate with filepond
      files: [],

      error: false,
      errorMessage: 'Please upload a file.',
      required: false,
      srId: 'pintura-uploader',

      // Pintura editor
      pinturaEditor: {
        // used to create the editor, receives editor configuration, should return an editor instance
        createEditor: openEditor,

        // Required, used for reading the image data
        imageReader: [
          createDefaultImageReader,
          {
            // https://pqina.nl/pintura/docs/v8/examples/load-heic-image/
            preprocessImageFile: async (file, options, onprogress) => {
              // If is not of type HEIC we skip the file
              if (!isHeicFile(file)) return file;

              // Turn the HEIC image into JPEG so the browser can read it
              return convertHeicToJpeg(file);
            },
          },
        ],

        // optionally. can leave out when not generating a preview thumbnail and/or output image
        // imageWriter: [createDefaultImageWriter],
        // optionally. can leave out when not generating a preview thumbnail and/or output image
        imageWriter: [
          createDefaultImageWriter,
        ],

        // used to generate poster images, runs an editor in the background
        imageProcessor: processImage,

        // editor options
        editorOptions: {
          imageCropAspectRatio: this.imageCropAspectRatio,
          cropSelectPresetOptions: [
            [undefined, 'Custom'],
            [1, 'Square'],
            [4 / 3, 'Landscape'],
            [3 / 4, 'Portrait'],
          ],
          imageOrienter: createDefaultImageOrienter(),
          locale: {
            ...locale_en_gb,
            ...plugin_crop_locale_en_gb,
          },
        },
      },
    };
  },
  computed: {
    inputSelector() {
      return `#${this.pondId}`;
    },
    bucket() {
      const bucketWithHost = this.endpoint.split('.')[0];
      return bucketWithHost.substr(8);
    },
  },
  created() {
    const vm = this;
    // loop through the photos passed in
    vm.existingAttachments.forEach((photo) => {
      vm.photos.push({
        id: photo.id,
        _destroy: false,
        amzDate: moment(),
        amzSignature: '',
        amzPolicy: '',
        amzKey: '',
        file_name: photo.name,
        s3_file_name: photo.key,
        status: 'uploaded',
      });

      const existingUrl = `${vm.cloudfrontBaseUrl}${photo.key}`;
      vm.files.push({
        // the server file reference
        source: existingUrl,
        // set type to local to indicate an already uploaded file
        options: {
          type: 'local',
          metadata: {
            poster: existingUrl,
          },
        },
      });
    });
  },
  mounted() {
    const vm = this;
    // Files the user has picked that haven't finished (or abandoned) their
    // journey yet — covers the Pintura editor being open, which starts at
    // add time, before any upload_pending photo exists for hosts to see.
    // Keyed by FilePond's file id; emitted as a single busy boolean.
    const busyFileIds = new Set();
    const emitBusy = () => vm.$emit('busy-changed', busyFileIds.size > 0);
    // Initial files (already-uploaded attachments) also fire addfilestart but
    // never process, so only user-picked input files count
    const isInputFile = (file) => file.origin === FilePond.FileOrigin.INPUT;

    FilePond.create(document.querySelector(this.inputSelector), {
      onaddfilestart: (file) => {
        if (!isInputFile(file)) return;
        busyFileIds.add(file.id);
        emitBusy();
      },
      // Fires on success AND failure — either way the editor/upload journey
      // is over (a retry re-enters via processfilestart)
      onprocessfile: (error, file) => {
        busyFileIds.delete(file.id);
        emitBusy();
      },
      onprocessfilestart: (file) => {
        if (!isInputFile(file)) return;
        busyFileIds.add(file.id);
        emitBusy();
      },
      // Editor cancel with instant edit removes the file
      onremovefile: (error, file) => {
        busyFileIds.delete(file.id);
        emitBusy();
      },
      filePosterMaxHeight: 512,
      imagePreviewHeight: 512,
      maxFiles: this.maxFiles,
      allowMultiple: this.maxFiles > 1,
      // Drag to reorder; the emitted photos array follows FilePond's order and
      // the backend stores that index as each photo's position
      allowReorder: this.maxFiles > 1,
      onreorderfiles: (files) => {
        vm.syncOrder(files);
        vm.emitChanges();
      },
      labelIdle: this.labelIdle,
      acceptedFileTypes: this.acceptedFileTypes,
      // Browsers often report an empty mime type for .heic (Windows) and for
      // vector art like .ai/.eps, which would fail the validation above
      fileValidateTypeDetectType: (source, type) => {
        if (type) return Promise.resolve(type);
        if (isHeicFile(source)) return Promise.resolve('image/heic');
        const extension = (source.name || '').split('.').pop().toLowerCase();
        const vectorTypes = { ai: 'application/postscript', eps: 'application/postscript', svg: 'image/svg+xml' };
        return Promise.resolve(vectorTypes[extension] || type);
      },
      maxFileSize: this.maxFileSize,
      credits: false,
      server: this.server,
      allowImageEditor: this.editImages,
      imageEditor: this.pinturaEditor,
      imageEditorInstantEdit: true,
      // Browsers can't render HEIC in an <img>, so without these two options the
      // image editor plugin skips Pintura processing and uploads the raw HEIC bytes.
      // With them, HEIC goes through preprocessImageFile (heic2any) and is written
      // out as a JPEG with crop/orientation baked in.
      imageEditorSupportImage: (file) => /^image/.test(file.type) || isHeicFile(file),
      imageEditorSupportImageFormat: (file) => isHeicFile(file),
      files: this.files,
    });
  },
  methods: {
    amzCredential(amzDate) {
      const date = amzDate.utc().format('YYYYMMDD');
      return `${this.accessKey}/${date}/us-west-2/s3/aws4_request`;
    },
    // I'm not sure why .toIsoFormat() didn't just work with aws s3 (maybe milliseconds), but this
    // is the closest format that matches their docs, which is a utc timestamp without any symbols in it
    amzDateAsLongISO(amzDate) {
      const utcIso = amzDate.utc().format('YYYYMMDDTHHmmss');
      return `${utcIso}Z`;
    },
    emitChanges() {
      this.$emit('photos-changed', this.photos);
    },
    // Reorder this.photos to match FilePond's file order. Processed files carry
    // the s3 key as serverId; initial (existing) files only have the cloudfront
    // url as source, which contains the key -- same matching as server.remove
    syncOrder(files) {
      const indexOf = (photo) => files.findIndex((file) => file.serverId === photo.s3_file_name
        || (typeof file.source === 'string' && file.source.includes(photo.s3_file_name)));
      const ordered = _.sortBy(this.photos, (photo) => {
        const index = indexOf(photo);
        return index === -1 ? Number.MAX_SAFE_INTEGER : index;
      });
      this.photos.splice(0, this.photos.length, ...ordered);
    },
    getClass() {
      if (this.error) {
        return 'has-error';
      }

      return '';
    },
  },
};
</script>
